import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { useLazyQuery } from '@apollo/react-hooks';
import { Descriptions, Input, Button, Tag, Collapse } from 'antd';
import { HeadingTwo } from 'Components/Headings';
import ContextSnackbar from 'Components/Snackbar';
import { displayFlex } from '../../../styles/Mixins';
import { paragraphReg } from '../../../styles/Typography';
import colors from '../../../styles/Colors';
import moment from 'moment';
import { SnackbarContext } from 'Store/SnackbarContext';
import { ORDER_FOR_SUPER_ADMIN } from 'Queries/Admin/OrderForSuperAdmin';

const { Panel } = Collapse;

function SuperAdminOrderPage(props) {
  const { className } = props;
  const [orderId, setOrderId] = useState('');
  const { showSnackbar } = useContext(SnackbarContext);
  const [getOrder, { data, loading }] = useLazyQuery(ORDER_FOR_SUPER_ADMIN, {
    fetchPolicy: 'network-only',
    onError: () => {
      showSnackbar('The order does not exist in the system', {
        duration: 3000,
        error: true
      });
    }
  });

  const handleGetOrder = async () => {
    if (orderId) {
      getOrder({
        variables: {
          id: +orderId
        }
      });
    }
  };

  const handleClear = () => {
    setOrderId('');
    // Reset data to hide any displayed order information
    getOrder({ variables: { id: null } });
  };

  const formatDate = date => {
    if (!date) return '';
    return moment(date).format('MM/DD/YYYY');
  };

  const renderBooleanTag = value => {
    return value ? <Tag color="green">True</Tag> : <Tag color="red">False</Tag>;
  };

  const renderStripeLink = ssChargeId => {
    if (!ssChargeId) return 'N/A';
    return (
      <a href={`https://dashboard.stripe.com/payments/${ssChargeId}`} target="_blank" rel="noopener noreferrer">
        {ssChargeId}
      </a>
    );
  };

  const order = data?.getOrderForSuperAdmin;

  return (
    <>
      <div className={className}>
        <ContextSnackbar />
        <div className={`${className}__page-header`}>
          <div className={`${className}__header-wrapper`}>
            <HeadingTwo label="ORDER" />
          </div>
        </div>
      </div>
      <div className={className}>
        <div className={`${className}__page-body`}>
          <div className={`${className}__selector-container`}>
            <div className={`${className}__selector-item`}>
              <h3>OrderId</h3>
              <div className="order-input">
                <Input
                  type="number"
                  onChange={e => setOrderId(e.target.value)}
                  value={orderId}
                  placeholder="OrderID"
                  style={{ width: '200px', marginRight: '10px' }}
                />
                <Button type="primary" disabled={loading} onClick={handleGetOrder} style={{ marginRight: '10px' }}>
                  Search
                </Button>
                <Button type="danger" onClick={handleClear}>
                  CLEAR
                </Button>
              </div>
            </div>

            {order && (
              <div className={`${className}__order-details`}>
                <Collapse defaultActiveKey={['1', '2', '3', '4', '5', '6']}>
                  <Panel header="General Information" key="1">
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Order ID">{order.id}</Descriptions.Item>
                      <Descriptions.Item label="Is Visited">{renderBooleanTag(order.isVisited)}</Descriptions.Item>
                      <Descriptions.Item label="Is Editable">{renderBooleanTag(order.isEditable)}</Descriptions.Item>
                      <Descriptions.Item label="Has Errors">{renderBooleanTag(order.hasErrors)}</Descriptions.Item>
                      <Descriptions.Item label="Canceled">{renderBooleanTag(order.canceled)}</Descriptions.Item>
                      <Descriptions.Item label="Fee">{order.fee}</Descriptions.Item>
                      <Descriptions.Item label="Platform Fee">{order.platformFee}</Descriptions.Item>
                      <Descriptions.Item label="Total">{order.total}</Descriptions.Item>
                      <Descriptions.Item label="Notes">{order.notes || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Admin Notes">{order.adminNotes || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Add-On Delivery Notes">{order.addOnDeliveryNotes || 'N/A'}</Descriptions.Item>
                      {order.group && <Descriptions.Item label="Group">{order.group.name}</Descriptions.Item>}
                      {order.successorOrder && <Descriptions.Item label="Successor Order ID">{order.successorOrder.id}</Descriptions.Item>}
                    </Descriptions>
                  </Panel>

                  <Panel header="Event Information" key="2">
                    {order.event ? (
                      <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="Event ID">{order.event.id}</Descriptions.Item>
                        <Descriptions.Item label="Event Name">{order.event.name}</Descriptions.Item>
                        <Descriptions.Item label="Open Date">{formatDate(order.event.openDate)}</Descriptions.Item>
                        <Descriptions.Item label="Close Date">{formatDate(order.event.closeDate)}</Descriptions.Item>
                        <Descriptions.Item label="Start Date">{formatDate(order.event.startDate)}</Descriptions.Item>
                        <Descriptions.Item label="End Date">{formatDate(order.event.endDate)}</Descriptions.Item>

                        {order.event.venue && (
                          <>
                            <Descriptions.Item label="Venue ID">{order.event.venue.id}</Descriptions.Item>
                            <Descriptions.Item label="Venue Name">{order.event.venue.name}</Descriptions.Item>
                          </>
                        )}
                      </Descriptions>
                    ) : (
                      <p>No event information available</p>
                    )}
                  </Panel>

                  <Panel header="Order Items" key="3">
                    {order.orderItems && order.orderItems.length > 0 ? (
                      order.orderItems.map((item, index) => (
                        <Descriptions key={item.id || index} bordered column={1} size="small" style={{ marginBottom: '20px' }}>
                          <Descriptions.Item label="Order Item ID">{item.id}</Descriptions.Item>
                          <Descriptions.Item label="Quantity">{item.quantity}</Descriptions.Item>

                          {item.addOnProduct && (
                            <>
                              <Descriptions.Item label="Add-On Product ID">{item.addOnProduct.id}</Descriptions.Item>
                              <Descriptions.Item label="Add-On Product Price">{item.addOnProduct.price}</Descriptions.Item>
                              {item.addOnProduct.addOn && (
                                <>
                                  <Descriptions.Item label="Add-On ID">{item.addOnProduct.addOn.id}</Descriptions.Item>
                                  <Descriptions.Item label="Add-On Name">{item.addOnProduct.addOn.name}</Descriptions.Item>
                                  <Descriptions.Item label="Add-On Unit Name">{item.addOnProduct.addOn.unitName}</Descriptions.Item>
                                </>
                              )}
                            </>
                          )}

                          {item.reservation && (
                            <>
                              <Descriptions.Item label="Reservation ID">{item.reservation.id}</Descriptions.Item>
                              <Descriptions.Item label="Start Date">{formatDate(item.reservation.startDate)}</Descriptions.Item>
                              <Descriptions.Item label="End Date">{formatDate(item.reservation.endDate)}</Descriptions.Item>

                              {item.reservation.stallProduct && (
                                <>
                                  <Descriptions.Item label="Stall Product ID">{item.reservation.stallProduct.id}</Descriptions.Item>
                                  <Descriptions.Item label="Stall Product Name">{item.reservation.stallProduct.name}</Descriptions.Item>
                                  <Descriptions.Item label="Stall Product Description">{item.reservation.stallProduct.description}</Descriptions.Item>
                                  <Descriptions.Item label="Stall Product Price">{item.reservation.stallProduct.price}</Descriptions.Item>
                                  <Descriptions.Item label="Stall Product Nightly">{renderBooleanTag(item.reservation.stallProduct.nightly)}</Descriptions.Item>
                                  <Descriptions.Item label="Stall Product Min Nights">{item.reservation.stallProduct.minNights}</Descriptions.Item>
                                </>
                              )}

                              {item.reservation.stalls && item.reservation.stalls.length > 0 && (
                                <Descriptions.Item label="Stalls">
                                  {item.reservation.stalls.map((stall, stallIndex) => (
                                    <div key={stall.id || stallIndex} style={{ marginBottom: '10px' }}>
                                      <p>
                                        <strong>ID:</strong> {stall.id}
                                      </p>
                                      <p>
                                        <strong>Name:</strong> {stall.name}
                                      </p>
                                      <p>
                                        <strong>Description:</strong> {stall.description}
                                      </p>
                                      <p>
                                        <strong>Status:</strong> {stall.status}
                                      </p>
                                      {stall.building && (
                                        <p>
                                          <strong>Building:</strong> {stall.building.name} (ID: {stall.building.id})
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </Descriptions.Item>
                              )}

                              {item.reservation.rvProduct && (
                                <>
                                  <Descriptions.Item label="RV Product ID">{item.reservation.rvProduct.id}</Descriptions.Item>
                                  <Descriptions.Item label="RV Product Name">{item.reservation.rvProduct.name}</Descriptions.Item>
                                  <Descriptions.Item label="RV Product Description">{item.reservation.rvProduct.description}</Descriptions.Item>
                                  <Descriptions.Item label="RV Product Price">{item.reservation.rvProduct.price}</Descriptions.Item>
                                  <Descriptions.Item label="RV Product Nightly">{renderBooleanTag(item.reservation.rvProduct.nightly)}</Descriptions.Item>
                                  <Descriptions.Item label="RV Product Min Nights">{item.reservation.rvProduct.minNights}</Descriptions.Item>

                                  {item.reservation.rvProduct.rvLot && (
                                    <>
                                      <Descriptions.Item label="RV Lot ID">{item.reservation.rvProduct.rvLot.id}</Descriptions.Item>
                                      <Descriptions.Item label="RV Lot Name">{item.reservation.rvProduct.rvLot.name}</Descriptions.Item>
                                      <Descriptions.Item label="RV Lot Description">{item.reservation.rvProduct.rvLot.description}</Descriptions.Item>
                                      <Descriptions.Item label="RV Lot Power">{renderBooleanTag(item.reservation.rvProduct.rvLot.power)}</Descriptions.Item>
                                      <Descriptions.Item label="RV Lot Sewer">{renderBooleanTag(item.reservation.rvProduct.rvLot.sewer)}</Descriptions.Item>
                                      <Descriptions.Item label="RV Lot Water">{renderBooleanTag(item.reservation.rvProduct.rvLot.water)}</Descriptions.Item>
                                    </>
                                  )}
                                </>
                              )}

                              {item.reservation.rvSpots && item.reservation.rvSpots.length > 0 && (
                                <Descriptions.Item label="RV Spots">
                                  {item.reservation.rvSpots.map((spot, spotIndex) => (
                                    <div key={spot.id || spotIndex} style={{ marginBottom: '10px' }}>
                                      <p>
                                        <strong>ID:</strong> {spot.id}
                                      </p>
                                      <p>
                                        <strong>Name:</strong> {spot.name}
                                      </p>
                                    </div>
                                  ))}
                                </Descriptions.Item>
                              )}

                              {item.reservation.status && (
                                <Descriptions.Item label="Reservation Status">
                                  {item.reservation.status.name} (ID: {item.reservation.status.id})
                                </Descriptions.Item>
                              )}
                            </>
                          )}
                        </Descriptions>
                      ))
                    ) : (
                      <p>No order items available</p>
                    )}
                  </Panel>

                  <Panel header="Payments" key="4">
                    {order.payments && order.payments.length > 0 ? (
                      order.payments.map((payment, index) => (
                        <Descriptions key={payment.id || index} bordered column={1} size="small" style={{ marginBottom: '20px' }}>
                          <Descriptions.Item label="Payment ID">{payment.id}</Descriptions.Item>
                          <Descriptions.Item label="Amount">{payment.amount}</Descriptions.Item>
                          <Descriptions.Item label="Created At">{formatDate(payment.createdAt)}</Descriptions.Item>
                          <Descriptions.Item label="Card Brand">{payment.cardBrand || 'N/A'}</Descriptions.Item>
                          <Descriptions.Item label="Card Payment">{renderBooleanTag(payment.cardPayment)}</Descriptions.Item>
                          <Descriptions.Item label="Last 4">{payment.last4 || 'N/A'}</Descriptions.Item>
                          <Descriptions.Item label="Notes">{payment.notes || 'N/A'}</Descriptions.Item>
                          <Descriptions.Item label="Service Fee">{payment.serviceFee}</Descriptions.Item>
                          <Descriptions.Item label="Stripe Fee">{payment.stripeFee}</Descriptions.Item>
                          <Descriptions.Item label="Stripe Charge ID">{renderStripeLink(payment.ssChargeId)}</Descriptions.Item>
                          <Descriptions.Item label="Stripe Refund ID">{payment.ssRefundId || 'N/A'}</Descriptions.Item>
                          <Descriptions.Item label="Success">{renderBooleanTag(payment.success)}</Descriptions.Item>
                          <Descriptions.Item label="Card Reader">{renderBooleanTag(payment.cardReader)}</Descriptions.Item>
                          <Descriptions.Item label="Admin Charge">{renderBooleanTag(payment.adminCharge)}</Descriptions.Item>
                          {payment.admin && <Descriptions.Item label="Admin ID">{payment.admin.id}</Descriptions.Item>}
                        </Descriptions>
                      ))
                    ) : (
                      <p>No payments available</p>
                    )}

                    {order.groupPayments && order.groupPayments.length > 0 && (
                      <>
                        <h4 style={{ marginTop: '20px' }}>Group Payments</h4>
                        {order.groupPayments.map((payment, index) => (
                          <Descriptions key={index} bordered column={1} size="small" style={{ marginBottom: '20px' }}>
                            <Descriptions.Item label="Amount">{payment.amount}</Descriptions.Item>
                            <Descriptions.Item label="Is Refund">{renderBooleanTag(payment.isRefund)}</Descriptions.Item>
                          </Descriptions>
                        ))}
                      </>
                    )}
                  </Panel>

                  <Panel header="User Information" key="5">
                    {order.user ? (
                      <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="User ID">{order.user.id}</Descriptions.Item>
                        <Descriptions.Item label="Email">{order.user.email}</Descriptions.Item>
                        <Descriptions.Item label="First Name">{order.user.firstName}</Descriptions.Item>
                        <Descriptions.Item label="Last Name">{order.user.lastName}</Descriptions.Item>
                        <Descriptions.Item label="Full Name">{order.user.fullName}</Descriptions.Item>
                        <Descriptions.Item label="Phone">{order.user.phone}</Descriptions.Item>
                      </Descriptions>
                    ) : (
                      <p>No user information available</p>
                    )}
                  </Panel>

                  <Panel header="Questions" key="6">
                    {(order.stallQuestionAnswers && order.stallQuestionAnswers.length > 0) ||
                    (order.rvQuestionAnswers && order.rvQuestionAnswers.length > 0) ? (
                      <>
                        {order.stallQuestionAnswers && order.stallQuestionAnswers.length > 0 && (
                          <>
                            <h4>Stall Questions</h4>
                            {order.stallQuestionAnswers.map((qa, index) => (
                              <Descriptions key={qa.id || index} bordered column={1} size="small" style={{ marginBottom: '20px' }}>
                                <Descriptions.Item label="Question ID">{qa.questionId}</Descriptions.Item>
                                <Descriptions.Item label="Question">{qa.question}</Descriptions.Item>
                                <Descriptions.Item label="Answer">{qa.answer}</Descriptions.Item>
                                <Descriptions.Item label="Question Type">{qa.questionType}</Descriptions.Item>
                                <Descriptions.Item label="Required">{renderBooleanTag(qa.required)}</Descriptions.Item>
                                {qa.minLength && <Descriptions.Item label="Min Length">{qa.minLength}</Descriptions.Item>}
                                {qa.maxLength && <Descriptions.Item label="Max Length">{qa.maxLength}</Descriptions.Item>}
                                {qa.answerOptions && <Descriptions.Item label="Answer Options">{qa.answerOptions}</Descriptions.Item>}
                              </Descriptions>
                            ))}
                          </>
                        )}

                        {order.rvQuestionAnswers && order.rvQuestionAnswers.length > 0 && (
                          <>
                            <h4>RV Questions</h4>
                            {order.rvQuestionAnswers.map((qa, index) => (
                              <Descriptions key={qa.id || index} bordered column={1} size="small" style={{ marginBottom: '20px' }}>
                                <Descriptions.Item label="Question ID">{qa.questionId}</Descriptions.Item>
                                <Descriptions.Item label="Question">{qa.question}</Descriptions.Item>
                                <Descriptions.Item label="Answer">{qa.answer}</Descriptions.Item>
                                <Descriptions.Item label="Question Type">{qa.questionType}</Descriptions.Item>
                                <Descriptions.Item label="Required">{renderBooleanTag(qa.required)}</Descriptions.Item>
                                {qa.minLength && <Descriptions.Item label="Min Length">{qa.minLength}</Descriptions.Item>}
                                {qa.maxLength && <Descriptions.Item label="Max Length">{qa.maxLength}</Descriptions.Item>}
                                {qa.answerOptions && <Descriptions.Item label="Answer Options">{qa.answerOptions}</Descriptions.Item>}
                              </Descriptions>
                            ))}
                          </>
                        )}
                      </>
                    ) : (
                      <p>No questions available</p>
                    )}
                  </Panel>
                </Collapse>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

const SuperAdminOrder = styled(SuperAdminOrderPage)`
  &&& {
    ${displayFlex}
    align-items: center;
    width: 100%;
    height: auto;
    padding: 12px 0 11px 20px;
    ${paragraphReg}
    font-size: 14px;
    text-align: left;
    background-color: ${colors.background.primary};
    color: ${colors.text};
    border-radius: 3px;
  }

  &__page-header {
    text-align: left;
    width: 100%;
    border-bottom: 1px solid rgb(200, 214, 229);
    &&& {
      margin: 65px 0 25px;
    }
  }

  &__page-body {
    text-align: left;
    width: 100%;
    &&& {
      margin: 0;
      padding: 10px;
    }

    h3 {
      text-transform: uppercase;
      font-family: IBMPlexSans-Bold;
      font-size: 12px;
      font-weight: 400;
      color: rgb(79, 93, 109);
      margin-top: 0px;
      margin-bottom: 3px;
    }
  }

  &__selector-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__selector-item {
    margin-bottom: 10px;
  }

  &__order-details {
    margin-top: 20px;
  }

  &__header-wrapper {
    margin: 20px 0px !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .order-input {
    display: flex;
    align-items: center;
  }

  .ant-descriptions-bordered .ant-descriptions-item-label {
    background-color: #fafafa;
    font-weight: bold;
  }
`;

export default SuperAdminOrder;
